/**
 * filter.js — K-053
 * Vanilla ES module. Responsibilities:
 *  1. URL sync  — filter state → history.pushState (Shopify URL format)
 *  2. AJAX refresh — fetch(currentUrl) → swap .collection__grid + updated filter panel
 *  3. popstate — back/forward button restores filter state
 *  4. Drawer toggle — mobile off-canvas <dialog>
 *  5. Focus management — after AJAX swap focus stays on interacted filter;
 *                        after drawer close focus returns to trigger
 *
 * No dependencies. No framework. No innerHTML for swapped content.
 */

(function () {
  'use strict';

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Collect all current filter inputs on the page into a URLSearchParams.
   * Handles checkbox list, boolean, and price range inputs.
   * Preserves any non-filter params already in the URL (page, sort_by, etc.)
   */
  function buildFilterParams() {
    var params = new URLSearchParams(window.location.search);

    // Remove all existing filter.* params so we rebuild from scratch
    var keysToDelete = [];
    params.forEach(function (val, key) {
      if (key.startsWith('filter.')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(function (k) { params.delete(k); });

    // Gather checked checkboxes and filled price inputs from all filter bars
    // (desktop bar + mobile drawer both live in the DOM; we deduplicate by param+value)
    var seen = new Set();
    document.querySelectorAll('[data-filter-input]').forEach(function (input) {
      if (input.type === 'checkbox') {
        if (input.checked) {
          var key = input.name + '=' + input.value;
          if (!seen.has(key)) {
            seen.add(key);
            params.append(input.name, input.value);
          }
        }
      }
    });

    // Price inputs are gathered separately via data-filter-price-min/max.
    // Skip empty values AND value="0" — the latter is what Liquid renders for
    // an unset `nil | divided_by: 100`, and submitting price.gte=0&price.lte=0
    // makes Shopify clamp results to $0 (returns no products + no active chips).
    function priceVal(input) {
      var v = input.value;
      if (v === '' || v == null) return null;
      var n = parseFloat(v);
      if (!isFinite(n) || n <= 0) return null;
      return Math.round(n * 100);
    }
    document.querySelectorAll('[data-filter-price-min]').forEach(function (input) {
      var cents = priceVal(input);
      if (cents !== null && !seen.has('price-min')) {
        seen.add('price-min');
        params.set(input.name, cents);
      }
    });
    document.querySelectorAll('[data-filter-price-max]').forEach(function (input) {
      var cents = priceVal(input);
      if (cents !== null && !seen.has('price-max')) {
        seen.add('price-max');
        params.set(input.name, cents);
      }
    });

    // Mobile drawer sort — checked radio in [data-drawer-sort] overrides sort_by.
    var checkedSort = document.querySelector('[data-drawer-sort]:checked');
    if (checkedSort) {
      params.set('sort_by', checkedSort.value);
    }

    // Reset page to 1 when filters change
    params.delete('page');

    return params;
  }

  /**
   * Perform AJAX refresh:
   *  1. Push new URL to history
   *  2. Fetch the new page
   *  3. Swap grid, pagination, filter panel (counts) and chips row
   */
  function applyFilters(triggerEl) {
    var params = buildFilterParams();
    var newUrl = window.location.pathname + '?' + params.toString();

    // Push URL immediately so the back button works
    history.pushState({ filterUrl: newUrl }, '', newUrl);

    fetchAndSwap(newUrl, triggerEl);
  }

  function fetchAndSwap(url, focusTarget) {
    // Find the filter bar to learn which section we're in
    var filterBar = document.querySelector('[data-filter-bar]');
    if (!filterBar) return;

    var sectionId = filterBar.dataset.sectionId;
    var gridId = filterBar.dataset.gridId || ('collection-grid-' + sectionId);
    var paginationId = filterBar.dataset.paginationId || ('collection-pagination-' + sectionId);

    // Show loading state
    var grid = document.getElementById(gridId);
    if (grid) {
      grid.setAttribute('aria-busy', 'true');
      grid.style.opacity = '0.5';
    }

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Filter fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Swap grid
        var newGrid = doc.getElementById(gridId);
        if (newGrid && grid) {
          grid.style.opacity = '';
          grid.removeAttribute('aria-busy');
          // adoptNode moves DOM nodes without innerHTML
          var adopted = document.adoptNode(newGrid);
          grid.parentNode.replaceChild(adopted, grid);
        } else if (grid) {
          grid.style.opacity = '';
          grid.removeAttribute('aria-busy');
        }

        // Swap pagination
        var newPagination = doc.getElementById(paginationId);
        var oldPagination = document.getElementById(paginationId);
        if (newPagination && oldPagination) {
          var adoptedPag = document.adoptNode(newPagination);
          oldPagination.parentNode.replaceChild(adoptedPag, oldPagination);
          // Re-bind load-more button in new pagination
          bindLoadMore();
        }

        // Swap filter bar (updates counts + active states)
        var newFilterBar = doc.querySelector('[data-filter-bar]');
        var oldFilterBar = document.querySelector('[data-filter-bar]');
        if (newFilterBar && oldFilterBar) {
          var adoptedBar = document.adoptNode(newFilterBar);
          oldFilterBar.parentNode.replaceChild(adoptedBar, oldFilterBar);
          bindFilterBar();
          bindSort();
        }

        // Swap chips row
        var newChips = doc.querySelector('[data-filter-chips]');
        var oldChips = document.querySelector('[data-filter-chips]');
        if (newChips && oldChips) {
          var adoptedChips = document.adoptNode(newChips);
          oldChips.parentNode.replaceChild(adoptedChips, oldChips);
          bindChips();
        }

        // Restore focus to the interacted filter element (by name attribute)
        if (focusTarget) {
          var name = focusTarget.name || focusTarget.dataset.filterParam;
          if (name) {
            var restored = document.querySelector('[name="' + CSS.escape(name) + '"]');
            if (restored) {
              restored.focus({ preventScroll: true });
            }
          }
        }
      })
      .catch(function (err) {
        if (grid) {
          grid.style.opacity = '';
          grid.removeAttribute('aria-busy');
        }
        console.error(err);
      });
  }

  // ─── Filter bar bindings ──────────────────────────────────────────────────

  function bindFilterBar() {
    // Dropdown triggers (desktop)
    document.querySelectorAll('[data-filter-trigger]').forEach(function (btn) {
      btn.addEventListener('click', onTriggerClick);
    });

    // Filter inputs — on change, apply immediately
    document.querySelectorAll('[data-filter-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        applyFilters(input);
      });
    });

    // Price apply buttons
    document.querySelectorAll('[data-filter-price-apply]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyFilters(btn);
      });
    });

    // Price inputs — apply on Enter
    document.querySelectorAll('[data-filter-price-min], [data-filter-price-max]').forEach(function (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          applyFilters(input);
        }
      });
    });

    // Mobile drawer open button
    document.querySelectorAll('[data-filter-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var drawerId = btn.dataset.filterDrawerOpen;
        openDrawer(drawerId, btn);
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', onOutsideClick, true);
  }

  function onTriggerClick(e) {
    var btn = e.currentTarget;
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;

    var isOpen = btn.getAttribute('aria-expanded') === 'true';

    // Close all other panels
    closeAllDropdowns();

    if (!isOpen) {
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  function closeAllDropdowns() {
    document.querySelectorAll('[data-filter-trigger]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('[data-filter-panel]').forEach(function (panel) {
      panel.hidden = true;
    });
  }

  function onOutsideClick(e) {
    var dropdown = e.target.closest('[data-filter-dropdown]');
    if (!dropdown) {
      closeAllDropdowns();
    }
  }

  // ─── Active filter chips bindings ─────────────────────────────────────────

  function bindChips() {
    // Remove individual filter chip
    document.querySelectorAll('[data-filter-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('data-filter-remove-price')) {
          // Remove price range
          document.querySelectorAll('[data-filter-price-min], [data-filter-price-max]').forEach(function (input) {
            input.value = '';
          });
        } else {
          var paramName = btn.dataset.filterParam;
          var paramValue = btn.dataset.filterValue;
          if (paramName) {
            // Uncheck matching checkbox
            document.querySelectorAll('[name="' + CSS.escape(paramName) + '"]').forEach(function (input) {
              if (input.type === 'checkbox') {
                if (paramValue) {
                  if (input.value === paramValue) input.checked = false;
                } else {
                  input.checked = false;
                }
              }
            });
          }
        }
        applyFilters(btn);
      });
    });

    // Clear all
    document.querySelectorAll('[data-filter-clear-all]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearAllFilters();
        applyFilters(btn);
      });
    });
  }

  function clearAllFilters() {
    document.querySelectorAll('[data-filter-input]').forEach(function (input) {
      if (input.type === 'checkbox') input.checked = false;
    });
    document.querySelectorAll('[data-filter-price-min], [data-filter-price-max]').forEach(function (input) {
      input.value = '';
    });
  }

  // ─── Drawer bindings ──────────────────────────────────────────────────────

  var _drawerTrigger = null;

  function openDrawer(drawerId, triggerEl) {
    var drawer = document.getElementById(drawerId);
    if (!drawer) return;
    _drawerTrigger = triggerEl || null;

    drawer.showModal();

    // ESC is native to <dialog>; we also handle backdrop click
    drawer.addEventListener('click', onDrawerBackdropClick);

    // Bind filter inputs inside drawer
    drawer.querySelectorAll('[data-filter-input]').forEach(function (input) {
      // Drawer inputs update immediately (they'll be applied on "Apply" or
      // individually via change — we bind change for auto-sync with desktop bar)
      input.addEventListener('change', function () {
        // Sync checked state to matching desktop bar input (same name+value)
        syncDrawerToBar(input);
      });
    });

    // Clear all inside drawer
    drawer.querySelectorAll('[data-filter-clear-all]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearAllFilters();
        closeDrawer(drawer);
        applyFilters(btn);
      });
    });

    // Apply / close buttons
    drawer.querySelectorAll('[data-filter-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyFilters(btn);
        closeDrawer(drawer);
      });
    });
  }

  function closeDrawer(drawer) {
    drawer.close();
    drawer.removeEventListener('click', onDrawerBackdropClick);
    // Return focus to trigger
    if (_drawerTrigger) {
      _drawerTrigger.focus({ preventScroll: true });
      _drawerTrigger = null;
    }
  }

  function onDrawerBackdropClick(e) {
    var drawer = e.currentTarget;
    var rect = drawer.querySelector('.collection-filter-drawer__inner');
    if (!rect) return;
    var bounds = rect.getBoundingClientRect();
    if (
      e.clientX < bounds.left ||
      e.clientX > bounds.right ||
      e.clientY < bounds.top ||
      e.clientY > bounds.bottom
    ) {
      closeDrawer(drawer);
    }
  }

  /**
   * When a drawer checkbox changes, mirror the state to the desktop bar
   * (same name + value) so buildFilterParams() reads a consistent state.
   */
  function syncDrawerToBar(drawerInput) {
    if (drawerInput.type !== 'checkbox') return;
    document.querySelectorAll('[data-filter-bar] [name="' + CSS.escape(drawerInput.name) + '"]').forEach(function (barInput) {
      if (barInput.value === drawerInput.value) {
        barInput.checked = drawerInput.checked;
      }
    });
  }

  // ─── Load-more (re-bind after pagination swap) ────────────────────────────

  function bindLoadMore() {
    document.querySelectorAll('[data-load-more]').forEach(function (btn) {
      // Remove old listeners by cloning
      var fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', onLoadMore);
    });
  }

  function onLoadMore() {
    var btn = this;
    var nextUrl = btn.dataset.nextUrl;
    var gridId = btn.dataset.gridId;
    var paginationId = btn.dataset.paginationId;
    if (!nextUrl || !gridId) return;

    btn.disabled = true;

    fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Append new products to existing grid
        var newGrid = doc.getElementById(gridId);
        var oldGrid = document.getElementById(gridId);
        if (newGrid && oldGrid) {
          Array.from(newGrid.children).forEach(function (child) {
            oldGrid.appendChild(document.adoptNode(child));
          });
        }

        // Swap pagination block
        var newPagination = doc.getElementById(paginationId);
        var oldPagination = document.getElementById(paginationId);
        if (newPagination && oldPagination) {
          var adopted = document.adoptNode(newPagination);
          oldPagination.parentNode.replaceChild(adopted, oldPagination);
          bindLoadMore();
        }
      })
      .catch(function (err) {
        console.error(err);
        btn.disabled = false;
      });
  }

  // ─── popstate — back/forward button restores filter state ────────────────

  window.addEventListener('popstate', function (e) {
    var url = (e.state && e.state.filterUrl) ? e.state.filterUrl : window.location.href;
    fetchAndSwap(url, null);
  });

  // ─── Sort dropdown ────────────────────────────────────────────────────────

  /**
   * Bind [data-collection-sort] <select> elements.
   * On change: update URL's sort_by param, then trigger the same AJAX refresh
   * used by filters so the grid swaps without a full page reload.
   * Called once on init; also called after filter AJAX swaps the filter bar
   * (which may contain a new sort select).
   */
  function bindSort() {
    document.querySelectorAll('[data-collection-sort]').forEach(function (select) {
      // Clone to shed any previously attached listeners before re-binding
      var fresh = select.cloneNode(true);
      select.parentNode.replaceChild(fresh, select);
      fresh.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', fresh.value);
        // Reset page to 1 on sort change
        params.delete('page');
        var newUrl = window.location.pathname + '?' + params.toString();
        // Without a filter bar, AJAX swap has nothing to target reliably —
        // just do a full navigation so the URL + grid stay in sync.
        if (!document.querySelector('[data-filter-bar]')) {
          window.location.href = newUrl;
          return;
        }
        history.pushState({ filterUrl: newUrl }, '', newUrl);
        fetchAndSwap(newUrl, null);
      });
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    // Sort can exist on collection pages without a filter bar (e.g. collections with no
    // declared filters). Bind it first so sort still works in that case.
    if (document.querySelector('[data-collection-sort]')) bindSort();
    if (!document.querySelector('[data-filter-bar]')) return;
    bindFilterBar();
    bindChips();
    bindLoadMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
